/**
 * Luxe · Phase 2B-2.5 — AI Conditioning Render Pass Manager.
 *
 * Generates six pixel-aligned deterministic passes from ONE frozen
 * camera snapshot:
 *
 *   • Beauty                — lit scene reference (client-safe render)
 *   • Depth                 — normalised near→far distance (RGB packed)
 *   • Normal                — world-space normal encoded as RGB
 *   • Object-ID             — segmentation, one stable colour per canonical item
 *   • Material/Surface-ID   — segmentation, one stable colour per canonical
 *                             (item, surface, bay, drawer/shelf) identity
 *   • Emissive              — lighting emphasis mask
 *
 * Design principles:
 *   1. All six passes are rendered from EXACTLY the same THREE.Camera
 *      instance. The camera is cloned + frozen before generation and
 *      user orbit is temporarily paused.
 *   2. ID passes disable antialias / tone mapping / fog / colour-space
 *      conversion so encoded colours are recoverable byte-for-byte.
 *   3. All passes use identical (width, height) and identical projection.
 *   4. Segmentation identity is deterministic across generations.
 */
import * as THREE from "three";

/**
 * Freeze a deterministic camera snapshot from a live THREE.Camera.
 * Every downstream pass renders through this same camera instance;
 * this metadata is what we persist server-side.
 */
export function freezeCameraSnapshot(camera) {
  camera.updateMatrixWorld(true);
  return {
    type: camera.isPerspectiveCamera ? "perspective" : "orthographic",
    position: [camera.position.x, camera.position.y, camera.position.z],
    quaternion: [camera.quaternion.x, camera.quaternion.y,
                 camera.quaternion.z, camera.quaternion.w],
    up: [camera.up.x, camera.up.y, camera.up.z],
    fov: camera.isPerspectiveCamera ? camera.fov : null,
    near: camera.near,
    far: camera.far,
    aspect: camera.aspect || null,
    zoom: camera.zoom,
    projection_matrix: camera.projectionMatrix.toArray(),
    matrix_world: camera.matrixWorld.toArray(),
  };
}

/** Deterministic 32-bit hash for a canonical string ID. */
export function idHash(s) {
  const str = String(s || "");
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h * 33) ^ str.charCodeAt(i)) >>> 0;
  return h >>> 0;
}

/** Assign a stable colour to every canonical Object-ID.  Returns
 *  { objectIdMap: {item_id: hex}, meshOverrideByItemId }. */
export function buildObjectIdMap(scene) {
  const map = {};
  scene.traverse((o) => {
    const ud = o.userData || {};
    const iid = ud.item_id;
    if (!iid || map[iid]) return;
    const h = idHash(iid);
    map[iid] = "#" + ("000000" + (h & 0xffffff).toString(16)).slice(-6);
  });
  return map;
}

/** Assign a stable colour to every canonical (item, surface) identity.
 *  Distinguishes Cabinet Top from Cabinet Carcass, individual Drawer
 *  Fronts, individual Shelves, individual Bay Fronts, Fireplace body,
 *  TV, Soundbar, Wall, Floor.  Even when several surfaces share the
 *  same material_id they receive independent segmentation IDs so
 *  Phase 2C AI can selectively edit each physical surface. */
export function buildMaterialSurfaceIdMap(scene) {
  const map = {};   // stable_id -> { colour, item_id, surface, resolved }
  scene.traverse((o) => {
    if (!o.isMesh) return;
    const ud = o.userData || {};
    const parent = o.parent?.userData || {};
    const iid = parent.item_id || ud.item_id;
    const scope = ud.scope || "surface";
    const bayIndex = parent.bay_index ?? null;
    const bayId    = parent.bay_id ?? null;
    const drawerId = ud.drawer_id ?? null;
    const shelfId  = ud.shelf_id  ?? null;
    const stable = [iid, scope,
      bayId != null ? `bay:${bayId}` : (bayIndex != null ? `bay#${bayIndex}` : null),
      drawerId ? `drawer:${drawerId}` : null,
      shelfId  ? `shelf:${shelfId}`   : null,
    ].filter(Boolean).join("::");
    if (!stable || map[stable]) return;
    const h = idHash(stable);
    map[stable] = {
      colour: "#" + ("000000" + (h & 0xffffff).toString(16)).slice(-6),
      item_id: iid || null,
      surface: scope,
      bay_id: bayId,
      bay_index: bayIndex,
      drawer_id: drawerId,
      shelf_id: shelfId,
      resolved: ud.resolved || null,
    };
  });
  return map;
}

/** Configure a renderer for discrete-ID passes.  Disables features
 *  that would smear IDs (anti-aliasing, tone mapping, sRGB conversion,
 *  fog, transparency blending). */
function _configureRendererForIdPass(renderer, scene) {
  const prev = {
    toneMapping:   renderer.toneMapping,
    outputColorSpace: renderer.outputColorSpace,
    background:    scene.background,
    fog:           scene.fog,
  };
  renderer.toneMapping     = THREE.NoToneMapping;
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  scene.background = new THREE.Color(0x000000);
  scene.fog        = null;
  return prev;
}
function _restoreRenderer(renderer, scene, prev) {
  renderer.toneMapping     = prev.toneMapping;
  renderer.outputColorSpace = prev.outputColorSpace;
  scene.background         = prev.background;
  scene.fog                 = prev.fog;
}

function _readPixelsAsBlob(renderer, w, h) {
  return new Promise((resolve) => {
    const canvas = renderer.domElement;
    canvas.toBlob((b) => resolve(b), "image/png");
  });
}

/** Render one pass to the offscreen canvas + return a PNG Blob. */
async function _renderAndSnapshot(renderer, scene, camera, w, h) {
  const prevSize = renderer.getSize(new THREE.Vector2());
  const prevPixelRatio = renderer.getPixelRatio();
  renderer.setPixelRatio(1);
  renderer.setSize(w, h, false);
  renderer.render(scene, camera);
  const blob = await _readPixelsAsBlob(renderer, w, h);
  renderer.setSize(prevSize.x, prevSize.y, false);
  renderer.setPixelRatio(prevPixelRatio);
  return blob;
}

/** Beauty — lit scene render (client-safe). */
async function beautyPass(renderer, scene, camera, w, h) {
  return _renderAndSnapshot(renderer, scene, camera, w, h);
}

/** Depth — normalised near→far distance packed into RGB (0..255).
 *  Uses ShaderMaterial override to write depth. */
async function depthPass(renderer, scene, camera, w, h) {
  const overrides = new Map();
  scene.traverse((o) => {
    if (o.isMesh) overrides.set(o, o.material);
  });
  const depthMat = new THREE.MeshDepthMaterial({
    depthPacking: THREE.RGBADepthPacking,
    blending: THREE.NoBlending,
  });
  overrides.forEach((_prev, o) => (o.material = depthMat));
  const prev = _configureRendererForIdPass(renderer, scene);
  const blob = await _renderAndSnapshot(renderer, scene, camera, w, h);
  _restoreRenderer(renderer, scene, prev);
  overrides.forEach((prevMat, o) => (o.material = prevMat));
  depthMat.dispose();
  return blob;
}

/** Normal — MeshNormalMaterial encodes world-space normal into RGB. */
async function normalPass(renderer, scene, camera, w, h) {
  const overrides = new Map();
  scene.traverse((o) => {
    if (o.isMesh) overrides.set(o, o.material);
  });
  const normalMat = new THREE.MeshNormalMaterial({ blending: THREE.NoBlending });
  overrides.forEach((_prev, o) => (o.material = normalMat));
  const prev = _configureRendererForIdPass(renderer, scene);
  const blob = await _renderAndSnapshot(renderer, scene, camera, w, h);
  _restoreRenderer(renderer, scene, prev);
  overrides.forEach((prevMat, o) => (o.material = prevMat));
  normalMat.dispose();
  return blob;
}

/** Object-ID — one flat colour per canonical item_id. */
async function objectIdPass(renderer, scene, camera, w, h, objectIdMap) {
  const overrides = new Map();
  const idMats    = new Map();
  scene.traverse((o) => {
    if (!o.isMesh) return;
    const parent = o.parent?.userData || {};
    const iid = parent.item_id || o.userData?.item_id;
    if (!iid || !objectIdMap[iid]) return;
    overrides.set(o, o.material);
    if (!idMats.has(iid)) {
      idMats.set(iid, new THREE.MeshBasicMaterial({
        color: new THREE.Color(objectIdMap[iid]),
        toneMapped: false,
        fog: false,
        transparent: false,
        blending: THREE.NoBlending,
      }));
    }
    o.material = idMats.get(iid);
  });
  const prev = _configureRendererForIdPass(renderer, scene);
  const blob = await _renderAndSnapshot(renderer, scene, camera, w, h);
  _restoreRenderer(renderer, scene, prev);
  overrides.forEach((prevMat, o) => (o.material = prevMat));
  idMats.forEach((m) => m.dispose());
  return blob;
}

/** Material/Surface-ID — one flat colour per (item, scope, bay, drawer, shelf). */
async function materialIdPass(renderer, scene, camera, w, h, matIdMap) {
  const overrides = new Map();
  const idMats    = new Map();
  scene.traverse((o) => {
    if (!o.isMesh) return;
    const ud = o.userData || {};
    const parent = o.parent?.userData || {};
    const iid = parent.item_id || ud.item_id;
    const scope = ud.scope || "surface";
    const bayIndex = parent.bay_index ?? null;
    const bayId    = parent.bay_id ?? null;
    const drawerId = ud.drawer_id ?? null;
    const shelfId  = ud.shelf_id  ?? null;
    const stable = [iid, scope,
      bayId != null ? `bay:${bayId}` : (bayIndex != null ? `bay#${bayIndex}` : null),
      drawerId ? `drawer:${drawerId}` : null,
      shelfId  ? `shelf:${shelfId}`   : null,
    ].filter(Boolean).join("::");
    const rec = matIdMap[stable];
    if (!rec) return;
    overrides.set(o, o.material);
    if (!idMats.has(stable)) {
      idMats.set(stable, new THREE.MeshBasicMaterial({
        color: new THREE.Color(rec.colour),
        toneMapped: false, fog: false, transparent: false,
        blending: THREE.NoBlending,
      }));
    }
    o.material = idMats.get(stable);
  });
  const prev = _configureRendererForIdPass(renderer, scene);
  const blob = await _renderAndSnapshot(renderer, scene, camera, w, h);
  _restoreRenderer(renderer, scene, prev);
  overrides.forEach((prevMat, o) => (o.material = prevMat));
  idMats.forEach((m) => m.dispose());
  return blob;
}

/** Emissive — meshes with emissive materials render bright, else black. */
async function emissivePass(renderer, scene, camera, w, h) {
  const overrides = new Map();
  scene.traverse((o) => {
    if (!o.isMesh) return;
    overrides.set(o, o.material);
    const m = o.material;
    const emissive = (m && m.emissive) ? m.emissive.clone() : new THREE.Color(0x000000);
    const intensity = (m && typeof m.emissiveIntensity === "number") ? m.emissiveIntensity : 1;
    const c = emissive.multiplyScalar(intensity);
    o.material = new THREE.MeshBasicMaterial({
      color: c, toneMapped: false, fog: false, blending: THREE.NoBlending,
    });
  });
  const prev = _configureRendererForIdPass(renderer, scene);
  const blob = await _renderAndSnapshot(renderer, scene, camera, w, h);
  _restoreRenderer(renderer, scene, prev);
  overrides.forEach((prevMat, o) => {
    if (o.material !== prevMat) o.material.dispose();
    o.material = prevMat;
  });
  return blob;
}

/**
 * Generate all six deterministic pixel-aligned passes.
 * @param {THREE.WebGLRenderer} renderer
 * @param {THREE.Scene} scene
 * @param {THREE.Camera} camera - MUST be the exact live camera used
 *   for beauty rendering; the manager freezes a snapshot before
 *   generation.
 * @param {number} width
 * @param {number} height
 * @returns {Promise<{cameraSnapshot, objectIdMap, materialSurfaceIdMap, passes: {name: Blob}}>}
 */
export async function generateAllPasses(renderer, scene, camera, width, height) {
  const cameraSnapshot = freezeCameraSnapshot(camera);
  const objectIdMap = buildObjectIdMap(scene);
  const materialSurfaceIdMap = buildMaterialSurfaceIdMap(scene);
  // NOTE: sequential; keeps the exact same camera transform throughout.
  const beauty  = await beautyPass(renderer, scene, camera, width, height);
  const depth   = await depthPass(renderer, scene, camera, width, height);
  const normal  = await normalPass(renderer, scene, camera, width, height);
  const objectId   = await objectIdPass(renderer, scene, camera, width, height, objectIdMap);
  const materialId = await materialIdPass(renderer, scene, camera, width, height, materialSurfaceIdMap);
  const emissive   = await emissivePass(renderer, scene, camera, width, height);
  return {
    cameraSnapshot,
    objectIdMap,
    materialSurfaceIdMap,
    width, height,
    passes: {
      beauty, depth, normal,
      object_id: objectId,
      material_id: materialId,
      emissive,
    },
  };
}
